// The evaluator. Runs every benefit config over a screener input, producing a
// three-state result per benefit with the rule that fired and the facts it read
// (the dev-panel trace). Then, with access to the full award picture, it
// resolves action states and the dependency chain — the thing production can't
// do, because its Apply signature never sees other results.

import {
  deriveFacts,
  type ScreenerInput,
} from '@/benefits-flow/engine/metrics';
import { M } from '@/benefits-flow/engine/metrics';
import { ALL_CONFIGS, B } from '@/benefits-flow/engine/rules';
import { BENEFITS } from '@/benefits-flow/engine/benefits';
import {
  dependencyNotes,
  heldAwards,
  resolveAction,
  type AwardToken,
  type DependencyNote,
} from '@/benefits-flow/engine/action';
import type {
  BenefitConfig,
  BenefitResult,
  Discrepancy,
  EligibilityStatus,
  Facts,
} from '@/benefits-flow/engine/types';

// Run a single config: rules in declaration order, first non-nil wins; all-nil
// → more-info-needed. Mirrors EligibilityConfig.Calculate, including building the
// relevant-facts subset per rule.
const runConfig = (
  config: BenefitConfig,
  facts: Facts,
): {
  status: EligibilityStatus;
  confidence: number | null;
  ruleKey?: string;
  relevantFacts: {
    key: string;
    value: BenefitResult['relevantFacts'][number]['value'];
  }[];
} => {
  for (const rule of config.rules) {
    const relevant: Facts = {};
    for (const k of rule.relevantKeys) {
      if (facts[k] !== undefined) relevant[k] = facts[k];
    }
    const outcome = rule.apply(relevant);
    if (outcome === null) continue;
    return {
      status: outcome.eligible ? 'eligible' : 'ineligible',
      confidence: outcome.confidence,
      ruleKey: rule.key,
      relevantFacts: rule.relevantKeys.map((k) => ({
        key: k,
        value: facts[k]?.value ?? null,
      })),
    };
  }
  return { status: 'more-info-needed', confidence: null, relevantFacts: [] };
};

// The five §7.3 bugs, as a catalogue the dev panel can render whether or not
// they fired. `fired` is computed per scenario in computeDiscrepancies.
export const DISCREPANCY_CATALOG: {
  id: string;
  benefitId: string;
  summary: string;
}[] = [
  {
    id: 'aa-ignores-6-month',
    benefitId: B.attendanceAllowance,
    summary:
      'Attendance Allowance never reads the 6-month qualifying period — it can show eligible even when the member said no to the duration question.',
  },
  {
    id: 'ctr-omits-ni',
    benefitId: B.councilTaxReduction,
    summary:
      'Council Tax Reduction’s residency gate omits Northern Ireland, so NI residents are wrongly ruled ineligible.',
  },
  {
    id: 'nhs-ignores-needs-nursing-care',
    benefitId: B.nhsFundedNursingCare,
    summary:
      'NHS-funded Nursing Care lists needs-nursing-care as relevant but never reads it — only being in a nursing home counts.',
  },
  {
    id: 'buspass-no-disability-route',
    benefitId: B.busPassScotland,
    summary:
      'Bus pass Scotland and NI have no disability route (TODO in source) — a disabled under-60 gets nothing on the age route.',
  },
  {
    id: 'pip-adp-no-lower-age',
    benefitId: B.pip,
    summary:
      'PIP and ADP have no lower age bound (real eligibility starts at 16), so an under-16 can show eligible.',
  },
];

const isTrue = (facts: Facts, key: string) => facts[key]?.value === true;
const isFalse = (facts: Facts, key: string) => facts[key]?.value === false;

// Which §7.3 bugs actually bit in this scenario.
const computeDiscrepancies = (
  input: ScreenerInput,
  facts: Facts,
  byId: Record<string, BenefitResult>,
): Discrepancy[] => {
  const fired: Discrepancy[] = [];
  const push = (catId: string) => {
    const def = DISCREPANCY_CATALOG.find((d) => d.id === catId);
    if (def)
      fired.push({
        id: def.id,
        benefitId: def.benefitId,
        summary: def.summary,
      });
  };

  // 1. AA eligible while the member answered "no" to having had the condition
  // 6 months (conditionDuration = neither → 6-month fact present and false).
  if (
    byId[B.attendanceAllowance]?.status === 'eligible' &&
    isFalse(facts, M.conditionDuration6Months)
  ) {
    push('aa-ignores-6-month');
  }

  // 2. NI resident ruled ineligible for CTR at the GB residency gate.
  if (
    isTrue(facts, M.northernIrelandResident) &&
    byId[B.councilTaxReduction]?.status === 'ineligible' &&
    byId[B.councilTaxReduction]?.ruleKey === 'not-gb-resident'
  ) {
    push('ctr-omits-ni');
  }

  // 3. Needs nursing care but not in a nursing home, resident E/W/NI, and NHS
  // FNC did not come back eligible — the needs-nursing-care answer was ignored.
  const residentEwni =
    isTrue(facts, M.englandResident) ||
    isTrue(facts, M.walesResident) ||
    isTrue(facts, M.northernIrelandResident);
  if (
    isTrue(facts, M.needsNursingCare) &&
    isFalse(facts, M.inNursingHome) &&
    residentEwni &&
    byId[B.nhsFundedNursingCare]?.status !== 'eligible'
  ) {
    push('nhs-ignores-needs-nursing-care');
  }

  // 4. Scotland/NI, has a bus-qualifying disability, under 60, and the bus pass
  // didn't come back eligible (no disability route exists).
  const busQualifying =
    isTrue(facts, M.isBlindOrPartiallySighted) ||
    isTrue(facts, M.isDeaf) ||
    isTrue(facts, M.withoutSpeech) ||
    isTrue(facts, M.hasLearningDisability) ||
    isTrue(facts, M.hasWalkingDisability) ||
    isTrue(facts, M.hasNoArms);
  const scotBus = byId[B.busPassScotland]?.status;
  const niBus = byId[B.busPassNorthernIreland]?.status;
  if (
    busQualifying &&
    isFalse(facts, M.over60) &&
    ((isTrue(facts, M.scotlandResident) && scotBus !== 'eligible') ||
      (isTrue(facts, M.northernIrelandResident) && niBus !== 'eligible'))
  ) {
    push('buspass-no-disability-route');
  }

  // 5. Under 16 and PIP or ADP came back eligible (no lower age bound).
  if (
    input.ageYears !== undefined &&
    input.ageYears < 16 &&
    (byId[B.pip]?.status === 'eligible' || byId[B.adp]?.status === 'eligible')
  ) {
    push('pip-adp-no-lower-age');
  }

  return fired;
};

export type Evaluation = {
  facts: Facts;
  results: BenefitResult[];
  byId: Record<string, BenefitResult>;
  eligibleIds: string[];
  held: Set<AwardToken>;
  dependencyNotes: DependencyNote[];
  discrepancies: Discrepancy[];
};

export type EvaluateOptions = {
  // Benefit ids marked awarded in the simulator (brief §7.5, §11).
  awarded?: Set<string>;
};

export const evaluate = (
  input: ScreenerInput,
  options: EvaluateOptions = {},
): Evaluation => {
  const facts = deriveFacts(input);
  const awarded = options.awarded ?? new Set<string>();
  const held = heldAwards(facts, awarded);

  // First pass — status + trace per benefit.
  const byId: Record<string, BenefitResult> = {};
  for (const config of ALL_CONFIGS) {
    const run = runConfig(config, facts);
    byId[config.id] = {
      benefitId: config.id,
      status: run.status,
      confidence: run.confidence,
      action: 'MORE_INFO_NEEDED',
      ruleKey: run.ruleKey,
      relevantFacts: run.relevantFacts,
      discrepancies: [],
    };
  }

  // Second pass — resolve actions with the full award picture.
  for (const id of Object.keys(byId)) {
    const r = byId[id];
    const resolved = resolveAction(id, r.status, held);
    r.action = resolved.action;
    r.actionReason = resolved.reason;
    r.route = resolved.route;
  }

  const discrepancies = computeDiscrepancies(input, facts, byId);
  for (const d of discrepancies) {
    byId[d.benefitId]?.discrepancies.push(d);
  }

  const results = ALL_CONFIGS.map((c) => byId[c.id]);
  const eligibleIds = results
    .filter((r) => r.status === 'eligible')
    .map((r) => r.benefitId);

  return {
    facts,
    results,
    byId,
    eligibleIds,
    held,
    dependencyNotes: dependencyNotes(held, new Set(eligibleIds)),
    discrepancies,
  };
};

// Total of the max annual estimates across eligible benefits — for the
// value-ranked variant and the scenario table. Always an estimate (brief §7.6).
export const totalEstimatedValue = (evaluation: Evaluation): number =>
  evaluation.eligibleIds.reduce((sum, id) => sum + (BENEFITS[id]?.max ?? 0), 0);
